import { useMemo } from 'react';
import { LineItemType } from '@lambda/apis/src/cart/types';
import { ProductSource } from '@/lib/analytics/types';
import { MAX_COMPATIBLE_PRODUCTS } from '@/lib/catalog/compatible-accessories';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import AccessoryGrid, {
  AccessoryGridPlaceholder,
} from '@/components/v2/purchase-flow/AccessoryGrid';
import InfiniteScrollTrigger from '@/components/v2/purchase-flow/InfiniteScrollTrigger';
import useCompatibleAccessories from '@/components/v2/purchase-flow/useCompatibleAccessories';
import i18n from '@/settings/i18n';
import { getCartLineCatalogProductId, isAccessoryLine } from './helpers';

const t = i18n.init('cart');

/**
 * Purchase flow only: the accessories compatible with any device in the cart,
 * as the accessories page's "More accessories" reads them for one, with
 * infinite scroll. Not tied to one device, so what's added here is its own
 * accessory line.
 */
const CartRecommendedAccessories = ({ className }: { className?: string }) => {
  const { cartLines } = useCartContext();

  // Each device by its product's MsCatalog2 id: a line without that note
  // (added before it existed) isn't asked about. Sorted, so the same devices
  // in another order don't start over.
  const productIds = useMemo(
    () =>
      Array.from(
        new Set(
          cartLines
            .filter(
              (line) =>
                line.type === LineItemType.PRODUCT && !isAccessoryLine(line),
            )
            .map(getCartLineCatalogProductId)
            .filter((id): id is string => Boolean(id)),
        ),
      )
        .sort()
        .slice(0, MAX_COMPATIBLE_PRODUCTS),
    [cartLines],
  );

  const {
    items: compatible,
    loadMore,
    hasMore,
    loading,
  } = useCompatibleAccessories(productIds);

  // TEMPORARY PATCH — knowingly breaks the CLAUDE.md "no offer/catalog business
  // logic in the storefront" rule, at the product team's request: what's
  // already in the cart is filtered out here. Remove once MsCatalog2's
  // compatibility endpoint can leave products out itself.
  //
  // Pages can come back short (or empty) as a result; the scroll trigger keeps
  // loading while it stays in view, so an emptied page doesn't stop paging.
  const inCart = useMemo(
    () =>
      new Set(
        cartLines
          .map(getCartLineCatalogProductId)
          .filter((id): id is string => Boolean(id)),
      ),
    [cartLines],
  );
  const items = compatible.filter(
    (sku) => !sku.product?.id || !inCart.has(sku.product.id),
  );

  if (items.length === 0 && !hasMore) {
    return null;
  }

  return (
    <section className={className}>
      {items.length > 0 && (
        <>
          <h2 className="mb-4 text-lg font-medium">{t`Recommended accessories`}</h2>
          <AccessoryGrid
            skus={items}
            source={ProductSource.CART_RECOMMENDATIONS}
            isAccessory
          />
        </>
      )}
      {/* The next row, spaced like the grid's own rows. */}
      {loading && (
        <div className={items.length > 0 ? 'mt-3 lg:mt-4' : ''}>
          <AccessoryGridPlaceholder />
        </div>
      )}
      {hasMore && <InfiniteScrollTrigger onVisible={loadMore} />}
    </section>
  );
};

export default CartRecommendedAccessories;
